/**
 * Compliance Service — API client for compliance document CRUD and status.
 *
 * Mirrors the backend ComplianceService endpoints for managing compliance
 * documents linked to vendors.
 *
 * Requirements: 9.1, 9.2
 */

import { apiClient } from '@/services/apiClient';
import type { ComplianceDocument } from '@/types';

// ─── Types ─────────────────────────────────────────────────────────────

export interface ComplianceDocListParams {
  documentType?: string;
  verificationStatus?: string;
  expirationStatus?: 'valid' | 'expiring' | 'expired';
}

export interface CreateComplianceDocInput {
  documentType: string;
  issueDate?: string | null;
  expirationDate?: string | null;
  egnyteFilePath?: string | null;
  egnyteFileId?: string | null;
  verificationStatus?: string | null;
}

export interface UpdateComplianceDocInput {
  documentType?: string;
  issueDate?: string | null;
  expirationDate?: string | null;
  egnyteFilePath?: string | null;
  egnyteFileId?: string | null;
  verificationStatus?: string | null;
  updatedAt: string;
}

export interface ComplianceDocTypeStatus {
  documentType: string;
  status: 'valid' | 'expiring' | 'expired' | 'missing';
  complianceDocId?: number;
  expirationDate?: string | null;
  verificationStatus?: string | null;
}

export interface ComplianceStatusSummary {
  vendorId: number;
  soc2Required: boolean;
  dataHandlingClass: string | null;
  documents: ComplianceDocTypeStatus[];
}

// ─── Constants ─────────────────────────────────────────────────────────

export const COMPLIANCE_DOCUMENT_TYPES = [
  'W-9',
  'SOC 2 Type II',
  'SOX Attestation',
  'Cyber Liability Insurance',
  'General Liability Insurance',
  'E&O Insurance',
  'Workers Comp Insurance',
  'BCP',
  'DRP',
] as const;

export const VERIFICATION_STATUSES = [
  'Verified',
  'Pending Verification',
  'Expired',
  'Not Submitted',
] as const;

// ─── Service ───────────────────────────────────────────────────────────

export const complianceService = {
  /** GET /api/vendors/:vendorId/compliance — List compliance docs for a vendor. */
  listDocuments(
    vendorId: number,
    params?: ComplianceDocListParams,
  ): Promise<ComplianceDocument[]> {
    return apiClient.get(
      `vendors/${vendorId}/compliance`,
      params as Record<string, string | number | boolean | undefined>,
    );
  },

  /** GET /api/vendors/:vendorId/compliance/status — Get compliance status summary. */
  getStatus(vendorId: number): Promise<ComplianceStatusSummary> {
    return apiClient.get(`vendors/${vendorId}/compliance/status`);
  },

  /** GET /api/compliance/:id — Get compliance doc detail. */
  getDocumentById(docId: number): Promise<ComplianceDocument> {
    return apiClient.get(`compliance/${docId}`);
  },

  /** POST /api/vendors/:vendorId/compliance — Create compliance doc. */
  createDocument(
    vendorId: number,
    input: CreateComplianceDocInput,
  ): Promise<ComplianceDocument> {
    return apiClient.post(`vendors/${vendorId}/compliance`, input);
  },

  /** PUT /api/compliance/:id — Update compliance doc. */
  updateDocument(
    docId: number,
    input: UpdateComplianceDocInput,
  ): Promise<ComplianceDocument> {
    return apiClient.put(`compliance/${docId}`, input);
  },

  /** DELETE /api/compliance/:id — Delete compliance doc. */
  deleteDocument(docId: number): Promise<void> {
    return apiClient.delete(`compliance/${docId}`);
  },
};
